import React from "react";
import { FileText, Plus, Trash2, Clock, ExternalLink } from "lucide-react";

/**
 * Document thumbnail card used on the Doc Builder home grid.
 * Shows the title, a short text excerpt and last-edited timestamp.
 */
export function DocThumb({ doc, active, canDelete, onOpen, onDelete }) {
  const excerpt = stripHtml(doc.content_html || "").slice(0, 90);
  const edited = doc.last_edited_at || doc.updated_at || doc.created_at;
  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid={`doc-thumb-${doc.doc_id}`}
      className={`group relative w-full text-left rounded-2xl border-2 overflow-hidden bg-white transition-all flex flex-col hover:-translate-y-0.5 hover:shadow-lg ${active ? "border-cyan-400 ring-2 ring-cyan-200" : "border-slate-200 hover:border-cyan-300"}`}
    >
      {/* A4 preview strip */}
      <div className="bg-gradient-to-b from-slate-50 to-white p-4 h-32 border-b border-slate-100 overflow-hidden relative">
        {doc.gdoc_thumbnail_url ? (
          // Live Google Drive thumbnail when we have it (admin GWS connected
          // + doc shared with admin). Falls back to text excerpt otherwise.
          <>
            <img
              src={doc.gdoc_thumbnail_url}
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
              referrerPolicy="no-referrer"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
            <span className="absolute top-2 right-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-sky-100 text-sky-700 font-semibold text-[9px] backdrop-blur-sm">
              <ExternalLink className="w-2.5 h-2.5" /> Google Doc
            </span>
          </>
        ) : (
          <>
            <div className="text-[10px] font-bold text-slate-300 mb-1.5 flex items-center gap-1">
              <FileText className="w-3 h-3" /> A4
              {doc.gdoc_web_url && (
                <span className="ml-auto inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-sky-100 text-sky-700 normal-case font-semibold text-[9px]">
                  <ExternalLink className="w-2.5 h-2.5" /> Google Doc
                </span>
              )}
            </div>
            <div className="text-[8px] leading-snug text-slate-500 line-clamp-5">
              {excerpt || <span className="italic text-slate-300">{doc.gdoc_id ? "Click to preview the Google Doc" : "Empty document"}</span>}
            </div>
          </>
        )}
      </div>
      <div className="p-3 flex-1 flex flex-col gap-1">
        <div className="font-semibold text-sm text-slate-800 truncate" title={doc.title || "Untitled"}>
          {doc.title || <span className="italic text-slate-400">Untitled</span>}
        </div>
        <div className="flex items-center justify-between gap-2 text-[10px] text-slate-400">
          <span className="flex items-center gap-1 truncate">
            <Clock className="w-3 h-3" /> {formatEdited(edited)}
          </span>
          {doc.last_edited_by_name && (
            <span className="truncate max-w-[80px]">· {doc.last_edited_by_name.split(" ")[0]}</span>
          )}
        </div>
      </div>
      {canDelete && (
        <span
          role="button"
          tabIndex={0}
          aria-label="Delete document"
          onClick={(e) => { e.stopPropagation(); e.preventDefault(); onDelete?.(); }}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); e.preventDefault(); onDelete?.(); } }}
          className="absolute top-2 right-2 p-1.5 rounded-lg bg-white/80 hover:bg-rose-50 hover:text-rose-600 text-slate-400 opacity-0 group-hover:opacity-100 transition cursor-pointer"
          data-testid={`doc-thumb-delete-${doc.doc_id}`}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </span>
      )}
    </button>
  );
}

export function NewDocThumb({ onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="doc-thumb-new"
      className="w-full rounded-2xl border-2 border-dashed border-cyan-300 bg-cyan-50/40 hover:bg-cyan-50 transition-all flex flex-col items-center justify-center p-6 min-h-[200px] hover:-translate-y-0.5 hover:shadow-lg"
    >
      <div className="w-12 h-12 rounded-2xl bg-cyan-500 text-white flex items-center justify-center mb-2 shadow">
        <Plus className="w-6 h-6" />
      </div>
      <div className="font-bold text-cyan-700 text-sm">New document</div>
      <div className="text-[11px] text-cyan-500/80">Start a blank page</div>
    </button>
  );
}

function stripHtml(html) {
  if (typeof window === "undefined") return html.replace(/<[^>]+>/g, "");
  const d = document.createElement("div");
  d.innerHTML = html;
  return (d.textContent || "").replace(/\s+/g, " ").trim();
}

function formatEdited(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const diffMs = Date.now() - d.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days} d ago`;
  return d.toLocaleDateString();
}
